/** Stored values for Category `eventCourseSubCategoryType` and the matching Page Designer enum. */
export const EVENT_COURSE_SUBCATEGORY_TYPE_VALUES = [
    'cooking-courses',
    'tasting-courses',
    'pastry-courses',
    'kids-courses',
    'creative-courses',
    'special-menus-and-dinners',
    'chef-dinners',
    'festivals-and-major-events',
    'music-and-cultural-events',
    'experiences-and-food-tours',
] as const;

export type EventCourseSubCategoryTypeId = (typeof EVENT_COURSE_SUBCATEGORY_TYPE_VALUES)[number];

/** Fallback page size when `search.products.hits.eventLimit` is unset. */
export const DEFAULT_EVENT_LISTING_HITS_LIMIT = 20;

type CategoryRef = {
    c_isCourseOrEvent?: boolean | null;
};

type ProductHitsConfig = {
    limit: number;
    eventLimit?: number;
    critical?: number;
    eventCritical?: number;
};

/** Client-safe predicate for event/course search layouts. */
export function isEventSearchLayout(layout: string | null | undefined): boolean {
    return layout === 'event';
}

/**
 * Hits per page for event/course listings; other listings use `hits.limit`.
 */
export function getEventListingHitsLimit(hitsConfig: ProductHitsConfig, isEventOrCourse: boolean): number {
    if (!isEventOrCourse) {
        return hitsConfig.limit;
    }
    return hitsConfig.eventLimit ?? DEFAULT_EVENT_LISTING_HITS_LIMIT;
}

/**
 * Blocking hits for the current listing layout. Event/course listings can use a separate count
 * to fill their wider desktop row; when unset, preserve the standard configured count.
 */
export function getEventListingCriticalCount(hitsConfig: ProductHitsConfig, isEventOrCourse: boolean): number {
    if (isEventOrCourse) {
        return hitsConfig.eventCritical ?? hitsConfig.critical ?? 4;
    }
    return hitsConfig.critical ?? 3;
}

/**
 * True when the current category has `c_isCourseOrEvent` set in Business Manager.
 * Used to switch PLP grids to the vertical-event tile layout and group variant hits.
 *
 * Accepts a plain object so raw SCAPI `Category` (before custom-attr intersection) is assignable.
 */
export function isEventOrCourseCategory(category: object | null | undefined): boolean {
    if (!category) {
        return false;
    }
    return (category as CategoryRef).c_isCourseOrEvent === true;
}
